import { bindable, customElement } from 'aurelia';
import template from './tag-modal.html';
import type { Tag, TagGroup } from '../services/api.types';
import { createTag, updateTag } from '../services/api.service';

@customElement({ name: 'tag-modal', template })
export class TagModal {
  @bindable public saved!: (tag: Tag) => void;
  @bindable public closed!: () => void;
  /** When provided the modal operates in edit mode */
  @bindable public tagToEdit: Tag | null = null;
  @bindable public groups: TagGroup[] = [];

  public name = '';
  public color = '#6366f1';
  public searchText = '';
  public groupId = '';
  public saving = false;
  public error = '';

  private _keyHandler!: (e: KeyboardEvent) => void;

  public get isEditMode(): boolean {
    return this.tagToEdit !== null;
  }

  public attached(): void {
    if (this.tagToEdit) {
      this.name = this.tagToEdit.name;
      this.color = this.tagToEdit.color;
      this.searchText = this.tagToEdit.searchText ?? '';
      this.groupId = this.tagToEdit.groupId ?? '';
    }
    this._keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this._keyHandler);
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
  }

  public close(): void {
    this.closed();
  }

  public async save(): Promise<void> {
    if (!this.name.trim()) {
      this.error = 'Name is required.';
      return;
    }
    this.saving = true;
    this.error = '';
    try {
      let tag: Tag;
      if (this.isEditMode && this.tagToEdit) {
        tag = await updateTag(this.tagToEdit.id, {
          name: this.name.trim(),
          color: this.color,
          searchText: this.searchText.trim(),
          groupId: this.groupId || null,
        });
      } else {
        tag = await createTag({
          name: this.name.trim(),
          color: this.color,
          searchText: this.searchText.trim(),
          groupId: this.groupId || null,
        });
      }
      this.saved(tag);
    } catch (e: unknown) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.saving = false;
    }
  }
}
